import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, X, ShieldCheck, Globe, Clock, KeyRound } from "lucide-react";

interface IpEntry {
  id: string;
  cidr: string;
  userId: string | null;
  userLabel: string | null;
}

interface WorkspaceUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  isActive?: boolean;
}

interface Settings {
  mfaRequiredForAdmins: boolean;
  mfaRequiredForAll: boolean;
  mfaAllowedMethods: ("totp" | "email" | "sms" | "passkey")[];
  mfaMinimumStrength: "any" | "no_sms" | "passkey_or_totp";
  mfaGracePeriodDays: number;
  sessionIdleTimeoutMinutes: number;
  sessionAdminIdleTimeoutMinutes: number;
  sessionAbsoluteMaxHours: number;
  ipAllowlistEnabled: boolean;
  ipAllowlistEntries: IpEntry[];
  ssoEnabled: boolean;
  ssoProvider: string | null;
  currentRequestIp: string | null;
  impossibleTravelKmh: number;
  volumetricExportMultiplier: number;
  volumetricExportFloor: number;
  securityEventRetentionDays: number;
}

const ALL_METHODS: Array<{ key: "totp" | "email" | "sms" | "passkey"; label: string }> = [
  { key: "totp", label: "Authenticator App (TOTP)" },
  { key: "passkey", label: "Passkey / Security Key" },
  { key: "email", label: "Email code" },
  { key: "sms", label: "SMS code" },
];

export function AdminSecuritySettingsCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Settings | null>(null);
  const [newCidr, setNewCidr] = useState("");
  const [assignUserId, setAssignUserId] = useState<string>("__shared__");

  const { data, isLoading } = useQuery<Settings>({ queryKey: ["/api/admin/security-settings"] });
  const { data: usersData } = useQuery<WorkspaceUser[]>({ queryKey: ["/api/admin/users"] });

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data]);

  // Sync ipAllowlistEntries from server into draft when it changes.
  useEffect(() => {
    if (data) {
      setDraft((prev) => prev ? { ...prev, ipAllowlistEntries: data.ipAllowlistEntries ?? [] } : prev);
    }
  }, [data?.ipAllowlistEntries]);

  const save = useMutation({
    mutationFn: async (payload: Partial<Settings>) => {
      const res = await apiRequest("PATCH", "/api/admin/security-settings", payload);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/security-settings"] });
      toast({ title: "Security settings saved" });
    },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const addEntry = useMutation({
    mutationFn: async ({ cidr, userId }: { cidr: string; userId: string | null }) => {
      const res = await apiRequest("POST", "/api/admin/security-settings/ip-entries", { cidr, userId });
      return res.json();
    },
    onSuccess: (data) => {
      setDraft((prev) => prev ? { ...prev, ipAllowlistEntries: data.entries ?? [] } : prev);
      qc.invalidateQueries({ queryKey: ["/api/admin/security-settings"] });
      setNewCidr("");
      setAssignUserId("__shared__");
      toast({ title: "IP entry added" });
    },
    onError: (e: any) => toast({ title: "Failed to add entry", description: e.message, variant: "destructive" }),
  });

  const removeEntry = useMutation({
    mutationFn: async (entryId: string) => {
      const res = await apiRequest("DELETE", `/api/admin/security-settings/ip-entries/${entryId}`);
      return res.json();
    },
    onSuccess: (data) => {
      setDraft((prev) => prev ? { ...prev, ipAllowlistEntries: data.entries ?? [] } : prev);
      qc.invalidateQueries({ queryKey: ["/api/admin/security-settings"] });
      toast({ title: "IP entry removed" });
    },
    onError: (e: any) => toast({ title: "Failed to remove entry", description: e.message, variant: "destructive" }),
  });

  if (isLoading || !draft) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const update = <K extends keyof Settings>(k: K, v: Settings[K]) => setDraft({ ...draft, [k]: v });

  const toggleMethod = (method: "totp" | "email" | "sms" | "passkey") => {
    const has = draft.mfaAllowedMethods.includes(method);
    const next = has
      ? draft.mfaAllowedMethods.filter((m) => m !== method)
      : [...draft.mfaAllowedMethods, method];
    update("mfaAllowedMethods", next);
  };

  const handleAddEntry = () => {
    const c = newCidr.trim();
    if (!c) return;
    const userId = assignUserId === "__shared__" ? null : assignUserId;
    addEntry.mutate({ cidr: c, userId });
  };

  const onSave = () => {
    // Send all draft fields — including the platform-only ones (SSO, anomaly
    // detection, retention) that are hidden from the tenant UI but still stored
    // by the backend. Their values come from the server and are preserved in
    // the draft so no data is silently lost on save.
    save.mutate({
      mfaRequiredForAdmins: draft.mfaRequiredForAdmins,
      mfaRequiredForAll: draft.mfaRequiredForAll,
      mfaAllowedMethods: draft.mfaAllowedMethods,
      mfaMinimumStrength: draft.mfaMinimumStrength,
      mfaGracePeriodDays: draft.mfaGracePeriodDays,
      sessionIdleTimeoutMinutes: draft.sessionIdleTimeoutMinutes,
      sessionAdminIdleTimeoutMinutes: draft.sessionAdminIdleTimeoutMinutes,
      sessionAbsoluteMaxHours: draft.sessionAbsoluteMaxHours,
      ipAllowlistEnabled: draft.ipAllowlistEnabled,
      // Platform-only fields: preserved as-is from server — not editable in
      // this tenant UI but still round-tripped so backend state is unchanged.
      ssoEnabled: draft.ssoEnabled,
      impossibleTravelKmh: draft.impossibleTravelKmh,
      volumetricExportMultiplier: draft.volumetricExportMultiplier,
      volumetricExportFloor: draft.volumetricExportFloor,
      securityEventRetentionDays: draft.securityEventRetentionDays,
    });
  };

  const entries: IpEntry[] = draft.ipAllowlistEntries ?? [];
  const activeUsers: WorkspaceUser[] = (usersData ?? []).filter((u: any) => u.isActive !== false);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          Security Settings
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Workspace-wide enforcement for multi-factor auth, session timeouts, and IP allow-list.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="w-4 h-4" /> Multi-Factor Authentication
          </CardTitle>
          <CardDescription>Decide who must enrol and which factor types are accepted.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Require MFA for admins</Label>
              <p className="text-xs text-muted-foreground">Admins must complete MFA at every sign-in.</p>
            </div>
            <Switch
              checked={draft.mfaRequiredForAdmins}
              onCheckedChange={(v) => update("mfaRequiredForAdmins", v)}
              data-testid="switch-mfa-required-admins"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Require MFA for everyone</Label>
              <p className="text-xs text-muted-foreground">All users must enrol within the grace period.</p>
            </div>
            <Switch
              checked={draft.mfaRequiredForAll}
              onCheckedChange={(v) => update("mfaRequiredForAll", v)}
              data-testid="switch-mfa-required-all"
            />
          </div>

          <div>
            <Label className="mb-2 block">Allowed factor types</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ALL_METHODS.map((m) => {
                const checked = draft.mfaAllowedMethods.includes(m.key);
                return (
                  <label
                    key={m.key}
                    className="flex items-center gap-2 border rounded-md p-2 cursor-pointer hover-elevate"
                    data-testid={`mfa-method-${m.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMethod(m.key)}
                    />
                    <span className="text-sm">{m.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Minimum strength</Label>
            <Select
              value={draft.mfaMinimumStrength}
              onValueChange={(v) => update("mfaMinimumStrength", v as Settings["mfaMinimumStrength"])}
            >
              <SelectTrigger className="mt-1" data-testid="select-mfa-min-strength">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any allowed factor</SelectItem>
                <SelectItem value="no_sms">Ban SMS-only (must also have TOTP, passkey, or email)</SelectItem>
                <SelectItem value="passkey_or_totp">Require passkey or authenticator app</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              "Ban SMS-only" stops users relying solely on SMS — they must add a stronger second factor.
            </p>
          </div>

          <div>
            <Label htmlFor="mfa-grace-days">Grace period (days)</Label>
            <Input
              id="mfa-grace-days"
              type="number"
              min={0}
              max={90}
              value={draft.mfaGracePeriodDays}
              onChange={(e) => update("mfaGracePeriodDays", Math.max(0, Math.min(90, parseInt(e.target.value || "0", 10))))}
              className="mt-1 max-w-[160px]"
              data-testid="input-mfa-grace-days"
            />
            <p className="text-xs text-muted-foreground mt-1">Days new users have to enrol before MFA blocks them.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4" /> Session timeouts
          </CardTitle>
          <CardDescription>Force re-authentication after inactivity.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="idle">Idle timeout (min)</Label>
            <Input
              id="idle"
              type="number"
              min={5}
              value={draft.sessionIdleTimeoutMinutes}
              onChange={(e) => update("sessionIdleTimeoutMinutes", parseInt(e.target.value || "0", 10))}
              className="mt-1"
              data-testid="input-session-idle"
            />
          </div>
          <div>
            <Label htmlFor="admin-idle">Admin idle timeout (min)</Label>
            <Input
              id="admin-idle"
              type="number"
              min={5}
              value={draft.sessionAdminIdleTimeoutMinutes}
              onChange={(e) => update("sessionAdminIdleTimeoutMinutes", parseInt(e.target.value || "0", 10))}
              className="mt-1"
              data-testid="input-session-admin-idle"
            />
          </div>
          <div>
            <Label htmlFor="abs">Absolute max (hours)</Label>
            <Input
              id="abs"
              type="number"
              min={1}
              value={draft.sessionAbsoluteMaxHours}
              onChange={(e) => update("sessionAbsoluteMaxHours", parseInt(e.target.value || "0", 10))}
              className="mt-1"
              data-testid="input-session-absolute"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="w-4 h-4" /> IP allow-list
          </CardTitle>
          <CardDescription>
            When enabled, only the IPs/CIDR ranges below can sign in. Entries can be shared
            (workspace-wide) or assigned to a specific user. Your current IP is{" "}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">{draft.currentRequestIp || "unknown"}</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Enforce IP allow-list</Label>
            <Switch
              checked={draft.ipAllowlistEnabled}
              onCheckedChange={(v) => {
                update("ipAllowlistEnabled", v);
                save.mutate({ ipAllowlistEnabled: v });
              }}
              data-testid="switch-ip-allowlist"
            />
          </div>

          {/* Existing entries */}
          <div className="space-y-2">
            {entries.length === 0 && (
              <p className="text-xs text-muted-foreground">No entries yet.</p>
            )}
            {entries.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-2 rounded-md border px-3 py-2"
                data-testid={`ip-entry-row-${e.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <code className="text-xs font-mono">{e.cidr}</code>
                  {e.userId ? (
                    <Badge variant="secondary" className="text-xs shrink-0">
                      {e.userLabel ?? e.userId}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs shrink-0">Shared</Badge>
                  )}
                </div>
                <button
                  onClick={() => removeEntry.mutate(e.id)}
                  disabled={removeEntry.isPending}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  data-testid={`button-remove-ip-entry-${e.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add new entry */}
          <div className="space-y-2 pt-1">
            <Label className="text-xs font-medium text-muted-foreground">Add an entry</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="e.g. 203.0.113.0/24 or 198.51.100.42"
                value={newCidr}
                onChange={(e) => setNewCidr(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddEntry();
                  }
                }}
                data-testid="input-new-cidr"
                className="flex-1"
              />
              <Select value={assignUserId} onValueChange={setAssignUserId}>
                <SelectTrigger className="w-full sm:w-52 shrink-0" data-testid="select-ip-entry-user">
                  <SelectValue placeholder="Shared (everyone)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__shared__">Shared — applies to everyone</SelectItem>
                  {activeUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`ip-user-option-${u.id}`}>
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.email || u.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={handleAddEntry}
                disabled={addEntry.isPending || !newCidr.trim()}
                data-testid="button-add-ip-entry"
                className="shrink-0"
              >
                {addEntry.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              "Shared" entries apply workspace-wide. Assigning an entry to a user restricts it to that person only — they are blocked if their IP matches neither a shared entry nor one of their own assigned ranges.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button onClick={onSave} disabled={save.isPending} data-testid="button-save-security">
          {save.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Save changes
        </Button>
      </div>
    </div>
  );
}
